import { useDecision } from '@optimizely/react-sdk';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
// import { ff_disableStrikePlacementAutomation } from 'app/constants/FeatureFlags/Placement/Keys';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import { orderPreferenceAlert } from '../OrderPreferenceAlerts';
import { AddOnContext } from '../OrderPreferencesWrapper';
import { orderPreferenceActions, placementStage } from '../store/OrderPreference.redux';
import {
  selectClientContacts,
  selectMiscellaneousDate,
  selectOrderPreferenceAlerts,
  selectSubmissionPreferences,
  selectUpdateStatus,
} from '../store/OrderPreference.selector';
import { SubmissionAutomationAction } from './AutomationActions/SubmissionAutomationAction';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ISubmissionEmail,
  ISubmissionPacket,
  getFormattedSubmissionEmail,
  getFormattedSubmissionPacket,
} from './SubmissionHelper';
import { ActionButtonWrapper } from '@AMIEWEB/Common/SubmissionPreference/ActionButtonWrapper/ActionButtonWrapper';
import { IOrderTargetMargin, orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import { ConfirmationDialog } from '@AMIEWEB/Settings/AutoTaskManagement/common/ConfirmationDialog';
import {
  selectConfirmationDialog,
  selectOrderDetailsUIView,
  selectOrderPayPackagesInteraction,
  selectPreferenceChanged,
  selectTabValue,
  selectTargetMarginOriginalResponse,
  selectTargetMarginResetResposne,
  selectTargetMargins,
} from '@AMIEWEB/Order/Store/Order.selectors';
import { useLocation, useNavigate, useBlocker } from 'react-router-dom';
import { selectPacketCategoryTitles } from 'store/redux-store/notification/notification.selector';
import { initialPacketState } from '../../../../Common/SubmissionPreference/SubmissionPacket/SubmissionPacketSelector';
import { initialSendPacketState } from '@AMIEWEB/Common/SubmissionPreference/SubmissionLimit/SendPacketAutomation';
import { validationSchema } from '@AMIEWEB/Common/SubmissionPreference/Common/validation';
import { yupResolver } from '@hookform/resolvers/yup';
import { ff_order_ui_newpreference_rfo_to_sent } from 'app/constants/FeatureFlags/Order/Keys';
import {
  checkSendToError,
  deriveAttentionToOpts,
  deriveSendToOpts,
  FormNames,
  isSendToExisted,
} from '@AMIEWEB/Common/SubmissionPreference/Common/helper';
import { Expandable } from '@AMIEWEB/Common';
import { Title } from '../customComponents/OrderPreferencesDesign';
import { isValidJSONString } from 'utils/string/string';
import { selectUser } from 'oidc/user.selectors';
import { INavigation, navigationStickActions } from '@AMIEWEB/GlobalNavigationMenu/NavigationStick.redux';
import { useReadOnly, userRoles } from 'oidc/userRoles';

export const ordersMenu: INavigation = {
  key: 'order',
  icon: 2,
  name: 'navStick.order',
  tag: 'order',
  path: '/search/order',
  roles: [userRoles.all],
};

import { SubmissionLimitsWrapper } from '@AMIEWEB/Common/SubmissionPreference/SubmissionLimit/SubmissionLimitsWrapper';
import { SubmissionPacketWrapper } from '@AMIEWEB/Common/SubmissionPreference/SubmissionPacket/SubmissionPacketWrapper';
import { SubmissionEmailWrapper } from '@AMIEWEB/Common/SubmissionPreference/SubmissionEmail/SubmissionEmailWrapper';
import { useConfirmExit } from 'utils/customHooks/useConfirmExit';
import { TargetMargins } from '../TargetMargins/TargetMargins';
import { ff_order_global_margintool, ff_order_global_margintool_ui } from 'app/constants/FeatureFlags/MarginTool/Keys';
import { hasAnyDirtyExcept, hasDirtyKey } from 'app/helpers/reactHookFormHelper';
import _ from 'lodash';
import { TargetMarginsResetter } from '../TargetMargins/TargetMarginsResetter';
import { OrderPayPackagesAction } from '@AMIEWEB/Order/Store/types';
import { transformTargetMargins } from '@AMIEWEB/Order/Store/helpers/sagaHelper';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { selectIsMarginOrderBaseDetailsValid } from '@AMIEWEB/Order/Store/Order.selectors';

export const SubmissionPreferences = () => {
  const { t } = useTranslation();
  const {
    rfoToSent,
    wfcToRfo,
    candidateTasks,
    isStrike,
    // disableAutomation,
    sendPacket: apiSendPacket,
    savePacket,
    submissionEmailSetup,
    submissionPacketSetup,
  } = useSelector(selectSubmissionPreferences);
  const clientContacts = useSelector(selectClientContacts);
  const categoryTitles = useSelector(selectPacketCategoryTitles);
  const targetMarginOriginalResponse = useSelector(selectTargetMarginOriginalResponse);
  const targetMarginResetResposne = useSelector(selectTargetMarginResetResposne);
  const orderPayPackagesInteraction = useSelector(selectOrderPayPackagesInteraction);

  const [openAutomationAction, setOpenAutomationAction] = React.useState<boolean>(false);
  const { promiseInProgress: updatingAutomationStatus } = usePromiseTracker({
    area: 'order-preference-update-automation-status-by-stage',
    delay: 0,
  });

  const { promiseInProgress: existingRFOsLoading } = usePromiseTracker({
    area: 'order-preference-get-existing-rfos',
    delay: 0,
  });
  const { promiseInProgress: pidsLoading } = usePromiseTracker({
    area: 'order-preference-get-ready-to-submit-rfo-pids',
    delay: 0,
  });
  const { promiseInProgress: activeCountsLoading } = usePromiseTracker({
    area: 'order-preference-active-placementcounts',
    delay: 0,
  });

  const { promiseInProgress: isSaving } = usePromiseTracker({
    area: `update-order-preferences-automation`,
    delay: 0,
  });
  const { promiseInProgress: isSavingTargetMargin } = usePromiseTracker({
    area: `update-order-target-margins`,
    delay: 0,
  });

  const { promiseInProgress: isFetchingSubmissionPref } = usePromiseTracker({
    area: `order-preference-get-automation-status-by-stage-${placementStage.submission}`,
    delay: 0,
  });

  const { promiseInProgress: isFetchingPref } = usePromiseTracker({
    area: 'order-preference-get-automation-status-by-stage',
    delay: 0,
  });
  const apiRefTargetMarginGrid = useGridApiRef();

  const isFetchingPreferences = useMemo(
    () => isFetchingSubmissionPref || isFetchingPref,
    [isFetchingSubmissionPref, isFetchingPref],
  );

  const {
    submissionLimits: { activePlacementCounts },
  } = useSelector(selectSubmissionPreferences);

  const { userInfo } = useSelector(selectUser);
  const alert = useSelector(selectOrderPreferenceAlerts);
  const { orderId } = useContext(AddOnContext);
  const inprogress = useSelector(selectUpdateStatus);
  const confirmationDialog = useSelector(selectConfirmationDialog);
  const tabValue = useSelector(selectTabValue);
  const preferenceChanged = useSelector(selectPreferenceChanged);
  const selectOrderUiDetails = useSelector(selectOrderDetailsUIView);
  const targetMargins = useSelector(selectTargetMargins);
  const user = useSelector(selectUser);
  const isMarginOrderBaseDetailsValid = useSelector(selectIsMarginOrderBaseDetailsValid);
  const { readOnly} = useReadOnly();


  // const [disableStrikePlacementDecision] = useDecision(ff_disableStrikePlacementAutomation);
  const [openClearChangesConfirmation, setOpenClearChangesConfirmation] = useState(false);
  const { disableEmailContainer, disablePacketContainer } = useSelector(selectMiscellaneousDate);
  const [rfoFlag] = useDecision(ff_order_ui_newpreference_rfo_to_sent);
  const [featureFlagOrderMarginTool] = useDecision(ff_order_global_margintool);
  const [featureFlagOrderMarginToolUI] = useDecision(ff_order_global_margintool_ui);
  const isResetActionTriggered = orderPayPackagesInteraction?.action === OrderPayPackagesAction.TARGET_MARGIN_RESET;

  const history = useNavigate();
  const dispatch = useDispatch();
  const [triggerExit, setTriggerExit] = useState({
    isExit: false,
    path: '',
  });

  // keeping default values to form
  const formMethods = useForm({
    defaultValues: {
      maxFilesSent: false,
      reason: null,
      submissionLimit: { noLimit: false, limit: null },
      wfcToRfo: false,
      candidateTasks: false,
      rfoToSent: false,
      sendPacket: { save: false, saveAndSend: false, doNotSave: false },
      packetTemplate: initialPacketState,
      emailSettings: '',
      emailTemplate: null,
      attentionTo: [],
      sendTo: [],
      noteToFacility: '',
      targetMargins: [],
    },
    resolver: !disableEmailContainer ? yupResolver(validationSchema) : null,
    mode: 'onChange',
  });

  const {
    formState: { dirtyFields, errors },
    reset,
    resetField,
    handleSubmit,
    getValues,
    trigger,
    watch,
  } = formMethods;

  const isDirty = (!featureFlagOrderMarginTool.enabled || !featureFlagOrderMarginToolUI.enabled)
    ? disableEmailContainer
      ? Object.keys(dirtyFields)?.filter(key => key !== FormNames.SendTo && key !== FormNames.TargetMargins)?.length !==
        0
      : Object.keys(dirtyFields).filter(key => key !== FormNames.TargetMargins)?.length !== 0
    : disableEmailContainer
    ? Object.keys(dirtyFields)?.filter(key => key !== FormNames.SendTo)?.length !== 0
    : Object.keys(dirtyFields)?.length !== 0;

 const isDirtyCombined = isDirty || isResetActionTriggered;
  //resetting form based on selector values
  const resetForm = React.useCallback(
    (isManualTrigger = false) => {
      const packetSetup: ISubmissionPacket = isValidJSONString(submissionPacketSetup)
        ? JSON.parse(submissionPacketSetup)
        : null;

      const emailSetup: ISubmissionEmail = isValidJSONString(submissionEmailSetup)
        ? JSON.parse(submissionEmailSetup)
        : null;

      const _sendPacket = rfoToSent
        ? {
            save: savePacket && !apiSendPacket,
            saveAndSend: apiSendPacket && savePacket,
            doNotSave: !savePacket,
          }
        : initialSendPacketState;

      const disablePacketContainer = !((_sendPacket.saveAndSend || _sendPacket.save) && rfoToSent && rfoFlag.enabled);
      dispatch(
        orderPreferenceActions.setMiscellaneousData({
          key: 'disablePacketContainer',
          value: disablePacketContainer,
        }),
      );

      const disableEmailContainer = !(_sendPacket.saveAndSend && rfoToSent && rfoFlag.enabled);
      dispatch(
        orderPreferenceActions.setMiscellaneousData({
          key: 'disableEmailContainer',
          value: disableEmailContainer,
        }),
      );

      const _attentionTo = deriveAttentionToOpts(clientContacts, packetSetup?.attentionTo, disablePacketContainer);

      const _sendTo = deriveSendToOpts(clientContacts, emailSetup?.sendTo, disableEmailContainer);

      if (!disableEmailContainer && !isSendToExisted(_sendTo)) {
        trigger(['sendTo']);
      }

      reset({
        wfcToRfo,
        candidateTasks,
        rfoToSent,
        sendPacket: _sendPacket,
        packetTemplate: disablePacketContainer ? initialPacketState : packetSetup?.packetTemplate,
        emailSettings: disableEmailContainer ? null : emailSetup?.bulkEmailSettings,
        emailTemplate: emailSetup?.emailTemplateId ?? categoryTitles?.[0]?.id ?? null,
        attentionTo: _attentionTo,
        sendTo: _sendTo,
        noteToFacility: disablePacketContainer ? '' : packetSetup?.noteToFacility,
        targetMargins:
          isResetActionTriggered && isManualTrigger
            ? transformTargetMargins(targetMarginOriginalResponse?.targetMarginOrders ?? []) //since we are resetting to original response , transform it
            : targetMargins,
      });
      //Reset the meta data associated with Reset Button inTarget Margins Grid
      if (isResetActionTriggered && isManualTrigger && (featureFlagOrderMarginTool.enabled && featureFlagOrderMarginToolUI.enabled)) {
        dispatch(orderDataActions.resetTargetMarginsMetaData());
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      apiSendPacket,
      candidateTasks,
      categoryTitles,
      clientContacts,
      rfoToSent,
      savePacket,
      submissionEmailSetup,
      submissionPacketSetup,
      wfcToRfo,
      rfoFlag,
      trigger,
      targetMargins,
      isResetActionTriggered,
      targetMarginOriginalResponse
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    () => resetForm(),
    [
      wfcToRfo,
      candidateTasks,
      rfoToSent,
      savePacket,
      apiSendPacket,
      submissionPacketSetup,
      submissionEmailSetup,
      rfoFlag,
      clientContacts,
    ],
  );
  const handleRfoToSentOnchange = value => {
    if (value) {
      setOpenAutomationAction(true);
    } else {
      confirmEnableAutomation(value);
    }
  };

  const SaveAutomation = () => {
    const data = getValues();
    const payload = {
      candidateTasks: data.candidateTasks,
      rfoToSent: data.rfoToSent,
      autoRFO: data?.wfcToRfo,
      sendPacket: data.sendPacket.saveAndSend,
      savePacket: data.sendPacket.save || data.sendPacket.saveAndSend,
      submissionEmailSetup: data.sendPacket.saveAndSend ? getFormattedSubmissionEmail(data) : null,
      submissionPacketSetup:
        data.sendPacket.save || data.sendPacket.saveAndSend ? getFormattedSubmissionPacket(data) : null,
      orderId,
      updatedBy: userInfo.employeeId,
    };
    if (!payload.sendPacket || (payload.sendPacket && data?.sendTo?.length > 0)) {
      updateOrderPref(payload);
    }
  };

  const handleGoToIntendedLocation = useCallback(
    location => {
      history(`${location}${tabValue !== 2 ? `?tab=${tabValue}` : ''}`);
    },
    [history, tabValue],
  );

  const { blockExit, allowExit } = useConfirmExit();
  const location = useLocation();

  const shouldBlock = useCallback(
    ({ nextLocation }) => {
      if (nextLocation.pathname !== location.pathname && isDirtyCombined) {
        return true;
      }
      return false;
    },
    [location.pathname, isDirtyCombined],
  );

  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (isDirtyCombined && blocker.state === 'blocked') {
      dispatch(orderDataActions.setConfirmationDialog({ ...confirmationDialog, open: true }));
    }
  }, [blocker, isDirtyCombined]);

  useEffect(() => {
    if (triggerExit.isExit && triggerExit.path) {
      handleGoToIntendedLocation(triggerExit.path);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleGoToIntendedLocation, triggerExit.isExit, triggerExit.path]);

  useEffect(() => {
    if (isDirtyCombined) {
      blockExit(() => {
        if (triggerExit.path !== '' && triggerExit.path !== location.pathname) {
          setTriggerExit(obj => ({ ...obj, path: location.pathname }));
          dispatch(orderDataActions.setConfirmationDialog({ ...confirmationDialog, open: true }));
        }
      });
    } else {
      allowExit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirtyCombined, location.pathname, blockExit, allowExit]);
  const onSubmit = data => {
    //save margin
    if (featureFlagOrderMarginTool.enabled && featureFlagOrderMarginToolUI.enabled) {
      if (hasDirtyKey(dirtyFields, FormNames.TargetMargins) || isResetActionTriggered) {
        const inactiveTargetMargins: IOrderTargetMargin[] = isResetActionTriggered
          ? (targetMarginResetResposne?.targetMarginOrders || []).filter(item => item.mode === 2)
          : [];
        const isRefreshFlow  = isResetActionTriggered ? targetMarginResetResposne?.isRefreshFlow : targetMarginOriginalResponse?.isRefreshFlow;
        const targetMarginOrderUpdateHistoryItems = isResetActionTriggered
          ? (targetMarginResetResposne?.targetMarginOrderUpdateHistoryItems || [])
          : (targetMarginOriginalResponse?.targetMarginOrderUpdateHistoryItems || []);
        const updatePayload = {
          orderId,
          userId: user?.userInfo?.employeeId,
          marginTargetDetails: {
            isRefreshFlow,
            targetMarginOrders: [...data.targetMargins, ...inactiveTargetMargins],
            targetMarginOrderUpdateHistoryItems,
          },
        };
        dispatch(
          orderDataActions.updateOrderTargetMargins({
            payload: updatePayload,
          }),
        );
        apiRefTargetMarginGrid?.current?.setSelectionModel([])
      }
    }

    if (isDirty && hasAnyDirtyExcept(dirtyFields, FormNames.TargetMargins)) {
      //dirty fields other than target margin
      if (data.rfoToSent !== rfoToSent) {
        handleRfoToSentOnchange(data.rfoToSent);
      } else {
        SaveAutomation();
      }
    }
  };

  const updateOrderPref = payload => {
    dispatch(
      orderPreferenceActions.updateOrderPreferences({
        data: payload,
      }),
    );
  };

  const confirmEnableAutomation = newAutomation => {
    SaveAutomation();
    setOpenAutomationAction(false);
  };

  useEffect(() => {
    alert === orderPreferenceAlert.automationOnSuccess && setOpenAutomationAction(false);
  }, [alert]);

  // #region : fix: Bug:496310
  // Track isDirtyCombined changes manually to keep preferenceChanged in Redux in sync.
  // We intentionally omit the deps array so this runs every render — bypassing React's
  // dep comparison mechanism, which is unreliable here because the isDirtyCombined
  // false→true transition happens inside a nested synchronous re-render triggered by
  // TargetMarginsResetter's useLayoutEffect (React 17 layout-flush edge case that
  // causes the dep-based useLayoutEffect to silently skip the re-run).
  // The prevIsDirtyCombinedRef guard ensures we only dispatch on genuine changes.
  // #endregion : fix: Bug:496310
  const prevIsDirtyCombinedRef = useRef(isDirtyCombined);
  useLayoutEffect(() => {
    const prev = prevIsDirtyCombinedRef.current;
    prevIsDirtyCombinedRef.current = isDirtyCombined;
    if (prev !== isDirtyCombined && isDirtyCombined !== preferenceChanged) {
      dispatch(orderDataActions.setIsPreferenceChaged(isDirtyCombined));
    }
  }); // intentionally no deps — manual change tracking via ref above

  useEffect(() => {
    /** Invoke service call if opened and automation is currently off */
    if (openAutomationAction && !rfoToSent) {
      const wfcToRfoChecked = watch('wfcToRfo');
      dispatch(orderPreferenceActions.getReadyToSubmitRfoPlacementIds({ orderId, isWfcToRfoChecked: wfcToRfoChecked }));
      dispatch(orderPreferenceActions.getExistingRFOs({ orderId }));
      dispatch(orderPreferenceActions.getActivePlacementCounts({ orderId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAutomationAction]);

  //handling modal action items::
  const handleKeepChanges = () => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
    dispatch(orderDataActions.setConfirmationDialog({ ...confirmationDialog, open: false }));
    setTriggerExit(obj => ({
      ...obj,
      path: location.pathname,
      isExit: false,
    }));
    dispatch(navigationStickActions.setSelectedMenu(ordersMenu));
  };

  const discardChanges = () => {
    dispatch(orderDataActions.setConfirmationDialog({ ...confirmationDialog, open: false }));
    dispatch(orderDataActions.setIsPreferenceChaged(false));
    dispatch(orderDataActions.setTabValue(confirmationDialog.tab));
    setTriggerExit(obj => ({
      ...obj,
      isExit: true,
      path: location.pathname,
    }));
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  };

  const handleClearChangesConfirmClose = (clearValues: boolean) => {
    setOpenClearChangesConfirmation(false);
    if (clearValues) {
      resetForm(clearValues);
      if(featureFlagOrderMarginTool.enabled && featureFlagOrderMarginToolUI.enabled) {
        apiRefTargetMarginGrid?.current?.setSelectionModel([]); //clear selection in grid
        dispatch(orderDataActions.setDirtyRows([])); //clear dirty rows in redux
      }
    }
  };

  const updateContainerState = (
    isEnabled: boolean,
    containerName: 'disableEmailContainer' | 'disablePacketContainer',
  ) => {
    dispatch(
      orderPreferenceActions.setMiscellaneousData({
        key: containerName,
        value: isEnabled,
      }),
    );
  };

  const formData = getValues();
  const sendToError = useMemo(() => checkSendToError(formData, errors), [errors, formData]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <React.Fragment>
          <Expandable expanded titleComponent={<Title>{t('Submission')}</Title>} unmountOnExit={false}>
            <LayoutGrid xs={13} direction="column" spacing={2}>
              <LayoutGridItem>
                <SubmissionLimitsWrapper
                  showActionItems={true}
                  isSaving={isSaving}
                  isFetchingPlacementCounts={activeCountsLoading}
                  activePlacementCounts={activePlacementCounts}
                  isStrike={isStrike}
                  submissionPacketSetup={submissionPacketSetup}
                  submissionEmailSetup={submissionEmailSetup}
                  isDirty={isDirty}
                  flag={ff_order_ui_newpreference_rfo_to_sent}
                  clientContacts={clientContacts}
                  isFetchingPreferences={isFetchingPreferences}
                  resetForm={resetForm}
                  updateContainerState={updateContainerState}
                />
              </LayoutGridItem>
              <LayoutGridItem>
                <SubmissionPacketWrapper disabled={disablePacketContainer} />
              </LayoutGridItem>
              <LayoutGridItem>
                <SubmissionEmailWrapper
                  disabled={disableEmailContainer}
                  facilityId={selectOrderUiDetails?.facilityId}
                />
              </LayoutGridItem>
            </LayoutGrid>
          </Expandable>
          {(featureFlagOrderMarginTool.enabled && featureFlagOrderMarginToolUI.enabled && (selectOrderUiDetails?.orderDetails?.isOrderMarginBaseDetailsIsValid === true || isMarginOrderBaseDetailsValid === true)) && (
            <LayoutGridItem>
              <TargetMargins apiRef={apiRefTargetMarginGrid} />
            </LayoutGridItem>
          )}
          {!(existingRFOsLoading || pidsLoading || activeCountsLoading) && (
            <SubmissionAutomationAction
              open={openAutomationAction}
              onCancel={() => setOpenAutomationAction(false)}
              onConfirm={() => confirmEnableAutomation(true)}
              isSubmittingAction={updatingAutomationStatus || inprogress}
            />
          )}
          <ActionButtonWrapper
            disableSubmit={!isDirtyCombined || sendToError || readOnly}
            disableCancel={!isDirtyCombined}
            isSaving={isSaving || isSavingTargetMargin}
            openClearChangesConfirmation={openClearChangesConfirmation}
            handleClearChanges={() => setOpenClearChangesConfirmation(true)}
            handleClearChangesConfirmClose={handleClearChangesConfirmClose}
          />
          <ConfirmationDialog
            dialogeOpen={confirmationDialog.open}
            keepChanges={handleKeepChanges}
            handleDiscard={discardChanges}
          />
          <TargetMarginsResetter />
          {/* //Component to handle the form reset on target margins update */}
        </React.Fragment>
      </form>
    </FormProvider>
  );
};
